import {
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router";
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  getGridSingleSelectOperators,
  getGridStringOperators,
  gridClasses,
} from "@mui/x-data-grid-pro";
import {
  CallFollowUpTypeLabels,
  CallMode,
  CallOutcomeLabels,
  CallStatus,
  ICallListItem,
  OrganisationFeatureFlags,
} from "shared";

import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useCurrentOrgUsers } from "../../hooks/useCurrentOrgUsers";
import { useUser } from "../../hooks/useUser";
import {
  useFetchCallTranscriptQuery,
  useFetchCallsQuery,
  useLazyFetchCallStatusQuery,
} from "../../services/api/contactsApi/contact";
import { WrappedLink } from "../WrappedLink";
import { Badge } from "../../../common/Atoms/Badge";
import { Drawer } from "../../../common/Atoms/Drawer";
import { MultiButtonCardHeader } from "../../../common/Atoms/MultiButtonCardHeader";
import { commonConfig, filterColumns } from "../../lib/datagrid";
import { DatagridCard } from "../Common/Datagrid/DatagridCard";
import {
  followUpBadgeColor,
  formatDuration,
  RecordedFollowUps,
  TranscriptBubbles,
} from "../Molecules/CallTranscript";

import { CallFollowUpEmail } from "./CallFollowUpEmail";
import { PhoneNumbers } from "./PhoneNumbers";
import { WalletBalance } from "./WalletBalance";

const STATUS_LABELS: Record<CallStatus, string> = {
  queued: `Queued`,
  ringing: `Ringing`,
  [`in-progress`]: `In progress`,
  completed: `Completed`,
  busy: `Busy`,
  [`no-answer`]: `No answer`,
  failed: `Failed`,
  canceled: `Canceled`,
};

const STATUS_COLORS: Partial<Record<CallStatus, `green` | `blue` | `red` | `gray`>> = {
  completed: `green`,
  queued: `blue`,
  ringing: `blue`,
  [`in-progress`]: `blue`,
  failed: `red`,
};

const LIVE_STATUSES: CallStatus[] = [`queued`, `ringing`, `in-progress`];

const MODE_LABELS: Record<CallMode, string> = {
  bridge: `Ring my phone`,
  browser: `Browser`,
};

const statusOptions = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));

function StatusBadge({ status }: { status: CallStatus }) {
  return (
    <Badge
      className={ `w-fit` }
      color={ STATUS_COLORS[status] || `gray` }
    >
      { STATUS_LABELS[status] || status }
    </Badge>
  );
}

export function CallsRoot() {
  const currentOrg = useGetSelectedOrganisation();
  const { organisationId } = useParams<{ organisationId: string }>();
  const userOptions = useCurrentOrgUsers(true);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });
  const [selected, setSelected] = useState<ICallListItem | null>(null);

  const filterValue = (field: string) => filterModel.items.find(item => item.field === field)?.value || null;

  const { data: user } = useUser();
  const [refreshCallStatus] = useLazyFetchCallStatusQuery();

  const { data, isLoading, isFetching, refetch } = useFetchCallsQuery({
    organisationId,
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    userId: filterValue(`userName`),
    status: filterValue(`status`),
    contactName: filterValue(`contactName`),
    searchText: filterModel.quickFilterValues?.[0] || null,
    sortColumn: sortModel[0]?.field || null,
    sortType: sortModel[0]?.sort || null,
  }, {
    skip: !organisationId,
    refetchOnMountOrArgChange: true,
  });

  // A call only moves on from Twilio when its status is polled, which stops once the call modal closes. Poll the
  // user's own unfinished rows (the status endpoint is owner only) and reload the list until they settle. Bounded:
  // Twilio ends every call, and a pending transcript turns processing or none within a minute of the call ending
  const staleCalls = (data?.calls || []).filter(row => row.userId === user?.id
    && (LIVE_STATUSES.includes(row.status) || row.transcriptStatus === `pending`));
  const staleKey = staleCalls.map(row => row.id).join();

  useEffect(() => {
    if (!organisationId || !staleCalls.length) return;

    const refresh = () => Promise.allSettled(staleCalls.map(row => refreshCallStatus({ organisationId, contactId: row.contactId, callId: row.id }).unwrap()))
      .then(() => refetch());

    refresh();

    const interval = setInterval(refresh, 10_000);

    return () => clearInterval(interval);
  }, [staleKey, organisationId]);

  const columns: GridColDef<ICallListItem>[] = useMemo(() => [
    {
      field: `createdAt`,
      headerName: `Date`,
      filterable: false,
      minWidth: 170,
      renderCell: ({ row }) => <>{ new Date(row.createdAt).toLocaleString() }</>,
    },
    {
      field: `contactName`,
      headerName: `Contact`,
      minWidth: 160,
      sortable: false,
      filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
      renderCell: ({ row }) => (
        <WrappedLink
          to={ `/contacts/${row.contactId}` }
          // On the Link, not the span inside it: stopping it inside means the Link never sees the click and the
          // browser does a full page load. Here it still navigates, just without also opening the drawer
          onClick={ e => e.stopPropagation() }
        >
          <span className={ `text-blue-600 underline whitespace-wrap` }>
            { row.contactName }
          </span>
        </WrappedLink>
      ),
    },
    {
      field: `personName`,
      headerName: `Person`,
      filterable: false,
      sortable: false,
      minWidth: 140,
      renderCell: ({ row }) => (
        <div className={ `leading-tight` }>
          <div>{ row.personName || `-` }</div>
          <div className={ `text-xs text-gray-500` }>{ row.toNumber }</div>
        </div>
      ),
    },
    {
      field: `userName`,
      headerName: `Called by`,
      type: `singleSelect`,
      sortable: false,
      minWidth: 140,
      filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
      valueOptions: userOptions,
      renderCell: ({ row }) => <>{ row.userName }</>,
    },
    {
      field: `mode`,
      headerName: `Mode`,
      filterable: false,
      renderCell: ({ row }) => <>{ MODE_LABELS[row.mode] || row.mode }</>,
    },
    {
      field: `status`,
      headerName: `Status`,
      type: `singleSelect`,
      filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
      valueOptions: statusOptions,
      renderCell: ({ row }) => <StatusBadge status={ row.status } />,
    },
    {
      field: `durationSeconds`,
      headerName: `Duration`,
      filterable: false,
      renderCell: ({ row }) => <>{ row.durationSeconds === null ? `-` : formatDuration(row.durationSeconds) }</>,
    },
    {
      field: `outcome`,
      headerName: `Outcome`,
      filterable: false,
      sortable: false,
      minWidth: 140,
      renderCell: ({ row }) => <>{ row.outcome ? CallOutcomeLabels[row.outcome] : `-` }</>,
    },
    {
      field: `followUps`,
      headerName: `Recorded`,
      filterable: false,
      sortable: false,
      minWidth: 170,
      renderCell: ({ row }) => (row.followUps.length ? (
        <div className={ `flex flex-wrap gap-1 py-0.5` }>
          { row.followUps.map((item, i) => (
            <Badge
              key={ `${item.type}-${item.invoiceId || i}` }
              color={ followUpBadgeColor[item.type] }
            >
              { item.invoiceId ? `${CallFollowUpTypeLabels[item.type]} · ${item.invoiceNumber || `invoice`}` : CallFollowUpTypeLabels[item.type] }
            </Badge>
          )) }
        </div>
      ) : <>{ `-` }</>),
    },
    {
      field: `summary`,
      headerName: `Summary`,
      filterable: false,
      sortable: false,
      flex: 1,
      minWidth: 250,
      renderCell: ({ row }) => (
        <span className={ `line-clamp-2 whitespace-normal text-gray-700` }>
          { row.summary || (row.transcriptStatus === `pending` || row.transcriptStatus === `processing` ? `Summarising...` : `-`) }
        </span>
      ),
    },
  ], [userOptions]);

  // The row as last fetched, so the drawer picks up anything recorded from it since it opened
  const selectedCall = (selected && data?.calls.find(row => row.id === selected.id)) || selected;

  if (!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.CLICK_TO_CALL)) {
    return null;
  }

  return (
    <div>
      <WalletBalance />
      <PhoneNumbers />
      <MultiButtonCardHeader
        title={ `Calls` }
        variant={ `plain` }
        description={ `Calls placed with click to call. Click a row to see the summary and transcript.` }
      />
      <DatagridCard className={ `mt-4` }>
        <div style={ { height: window.innerHeight - 180 } }>
          <DataGridPro
            sx={ {
              ...commonConfig.headerStyle,
              border: `none`,
              [`& .${gridClasses.cell}`]: { py: 1 },
              [`& .${gridClasses.row}`]: { cursor: `pointer` },
            } }
            rows={ data?.calls || [] }
            columns={ columns }
            getRowHeight={ () => `auto` }
            loading={ isLoading || isFetching }
            onRowClick={ ({ row }) => setSelected(row) }
            pageSizeOptions={ [20, 50, 100, 300] }
            disableDensitySelector
            pagination
            paginationMode={ `server` }
            rowCount={ data?.count ?? 0 }
            paginationModel={ paginationModel }
            onPaginationModelChange={ setPaginationModel }
            filterDebounceMs={ 300 }
            filterMode={ `server` }
            filterModel={ filterModel }
            onFilterModelChange={ model => {
              setFilterModel(model);
              // A narrower filter can leave the current page past the last result
              setPaginationModel(p => ({ ...p, page: 0 }));
            } }
            sortingMode={ `server` }
            sortModel={ sortModel }
            onSortModelChange={ setSortModel }
            showToolbar
            slots={ { toolbar: CallsToolbar } }
            slotProps={ {
              pagination: { showFirstButton: true, showLastButton: true },
              filterPanel: { filterFormProps: { filterColumns: args => filterColumns(args, []) } },
            } }
          />
        </div>
      </DatagridCard>
      <Drawer
        title={ selectedCall ? `Call with ${selectedCall.contactName}` : `` }
        open={ !!selectedCall }
        setOpen={ open => !open && setSelected(null) }
      >
        { selectedCall && organisationId ? (
          <CallDetail
            key={ selectedCall.id }
            call={ selectedCall }
            organisationId={ organisationId }
            onFollowUpSent={ refetch }
          />
        ) : null }
      </Drawer>
    </div>
  );
}

function CallDetail({ call, organisationId, onFollowUpSent }: { call: ICallListItem; organisationId: string; onFollowUpSent: () => void }) {
  const { data, isFetching, isError } = useFetchCallTranscriptQuery({
    organisationId,
    contactId: call.contactId,
    callId: call.id,
  });

  const details: [string, ReactNode][] = [
    [`Contact`, (
      <WrappedLink
        key={ `contact` }
        to={ `/contacts/${call.contactId}` }
      >
        <span className={ `text-blue-600 underline` }>{ call.contactName }</span>
      </WrappedLink>
    )],
    [`Person`, `${call.personName || `-`} (${call.toNumber})`],
    [`Called by`, call.userName],
    [`Date`, new Date(call.createdAt).toLocaleString()],
    [`Mode`, MODE_LABELS[call.mode] || call.mode],
    [`Status`, <StatusBadge
      key={ `status` }
      status={ call.status } />],
    [`Duration`, call.durationSeconds === null ? `-` : formatDuration(call.durationSeconds)],
  ];

  return (
    <div className={ `space-y-6` }>
      <dl className={ `grid grid-cols-3 gap-x-4 gap-y-2 text-sm` }>
        { details.map(([label, value]) => (
          <div
            key={ label }
            className={ `col-span-3 grid grid-cols-3` }
          >
            <dt className={ `text-gray-500` }>{ label }</dt>
            <dd className={ `col-span-2 text-gray-900` }>{ value }</dd>
          </div>
        )) }
      </dl>

      <div>
        <h3 className={ `mb-2 text-sm font-semibold text-gray-900` }>{ `Summary` }</h3>
        <p className={ `whitespace-pre-wrap text-sm text-gray-700` }>{ call.summary || `No summary for this call.` }</p>
      </div>

      <div>
        <h3 className={ `mb-2 text-sm font-semibold text-gray-900` }>{ `Recorded from this call` }</h3>
        { call.followUps.length ? (
          <RecordedFollowUps items={ call.followUps } />
        ) : (
          <p className={ `text-sm text-gray-500` }>{ `No payment promises, queries, plans or emails were recorded from this call.` }</p>
        ) }
      </div>

      { call.transcriptStatus === `completed` ? (
        <div>
          <h3 className={ `mb-2 text-sm font-semibold text-gray-900` }>{ `Follow up email` }</h3>
          <CallFollowUpEmail
            call={ call }
            organisationId={ organisationId }
            onSent={ onFollowUpSent }
          />
        </div>
      ) : null }

      <div>
        <h3 className={ `mb-2 text-sm font-semibold text-gray-900` }>{ `Transcript` }</h3>
        <div className={ `rounded-lg border border-gray-200 bg-gray-50 p-3` }>
          { data?.transcript?.length ? (
            <TranscriptBubbles
              segments={ data.transcript }
              userLabel={ call.userName || `Caller` }
              contactLabel={ call.personName || `Contact` }
            />
          ) : (
            <p className={ `text-xs text-gray-500` }>
              { isFetching ? `Loading transcript...` : isError ? `Could not load the transcript.` : `No transcript for this call.` }
            </p>
          ) }
        </div>
      </div>
    </div>
  );
}

function CallsToolbar() {
  return (
    <GridToolbarContainer sx={ { justifyContent: `flex-start` } }>
      <GridToolbarQuickFilter
        // Search the whole term, not each word separately
        quickFilterParser={ value => value.trim() ? [value.trim()] : [] }
        debounceMs={ 350 }
        slotProps={ { root: { placeholder: `Search contact, person, number or summary...` } } }
      />
      <GridToolbarFilterButton />
      <GridToolbarColumnsButton />
    </GridToolbarContainer>
  );
}
